/**
 * olyonze — buy-box add-on.
 *
 * Two buy paths have to be handled, and they work very differently.
 *
 * 1. "Adicionar ao carrinho" — the Horizon product form intercepts its own submit and
 *    renders the cart from the response, so instead of fighting it we add the companion
 *    line FIRST and then let the normal add run untouched. The cart the customer sees then
 *    already contains both lines.
 *
 * 2. "Compre já" — Shopify's accelerated checkout button skips the cart entirely and builds
 *    a checkout from the form's single variant, so a companion line can never ride along.
 *    When the box is ticked we divert: put both lines in the cart, then send the customer to
 *    /checkout. Still one tap, and the add-on actually gets paid for. When the box is NOT
 *    ticked the native express flow is left completely alone (it keeps its wallet fast path).
 *
 * If either companion add fails, the customer's purchase still proceeds — we never block a
 * sale over the attachment.
 */

const BUSY_CLASS = 'product-addon--busy';
const ADDON_SELECTOR = '.product-addon';
const SUBMIT_SELECTOR = 'button[name="add"], button[type="submit"]';
const EXPRESS_SELECTOR = '.shopify-payment-button__button';

function cartUrl(path) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}${path}`.replace(/\/{2,}/g, '/');
}

async function addToCart(items) {
  const response = await fetch(cartUrl('cart/add.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error(`cart/add.js responded ${response.status}`);
  return response.json();
}

function readMainLine(form) {
  const id = Number(form.querySelector('[name="id"]')?.value);
  const rawQuantity = Number(form.querySelector('[name="quantity"]')?.value);
  const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? rawQuantity : 1;
  return Number.isFinite(id) && id > 0 ? { id, quantity } : null;
}

function setup(root) {
  const variantId = Number(root.dataset.addonVariantId);
  const checkbox = root.querySelector('.product-addon__checkbox');
  const form = root.closest('form[action*="/cart/add"]');
  if (!variantId || !checkbox || !form) return;

  const addonLine = { id: variantId, quantity: 1 };
  let passthrough = false;

  // Delegated on the form: Shopify re-renders the accelerated-checkout button
  // asynchronously, so a listener bound straight to it would be lost.
  form.addEventListener(
    'click',
    async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const isSubmit = target.closest(SUBMIT_SELECTOR);
      const isExpress = target.closest(EXPRESS_SELECTOR);
      if (!isSubmit && !isExpress) return;

      // Our own re-dispatched click — let the theme handle it this time.
      if (passthrough) {
        passthrough = false;
        return;
      }
      if (!checkbox.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      root.classList.add(BUSY_CLASS);

      if (isExpress) {
        // Express buy bypasses the cart, so build the cart ourselves and go to checkout.
        const mainLine = readMainLine(form);
        try {
          await addToCart(mainLine ? [mainLine, addonLine] : [addonLine]);
          checkbox.checked = false;
          window.location.href = cartUrl('checkout');
          return;
        } catch (error) {
          console.error('[olyonze addon] express checkout diversion failed', error);
          // Fall back to the native express flow rather than blocking the sale.
          root.classList.remove(BUSY_CLASS);
          passthrough = true;
          isExpress.click();
          return;
        }
      }

      try {
        await addToCart([addonLine]);
        // Only clear the choice once the line is really in the cart, so a failure leaves
        // it visible instead of silently dropping it.
        checkbox.checked = false;
      } catch (error) {
        console.error('[olyonze addon] could not add the companion product', error);
      } finally {
        root.classList.remove(BUSY_CLASS);
        passthrough = true;
        isSubmit.click();
      }
    },
    true // capture, so we run before the theme's and Shopify's own handlers
  );
}

function init() {
  document.querySelectorAll(ADDON_SELECTOR).forEach((root) => {
    if (root.dataset.addonReady === 'true') return;
    root.dataset.addonReady = 'true';
    setup(root);
  });
}

init();
// Section rendering (variant changes, cart updates) can replace the buy box.
document.addEventListener('shopify:section:load', init);
